import { NavLink } from 'react-router-dom';
import { routePaths } from '@/app/routes';

/**
 * Public gralmedical.ro marketing site — every header link points here. The
 * client-area is a separate app, so these are absolute, open-in-new-tab links.
 */
const SITE = 'https://www.gralmedical.ro';

const CALL_CENTER = { label: '021-323.00.00', tel: '0213230000' };

/** Top utility row (right-aligned small links), mirroring gralmedical.ro. */
const TOP_LINKS: { label: string; href: string }[] = [
  { label: 'Despre noi', href: `${SITE}/despre-noi` },
  { label: 'Programari Online', href: `${SITE}/Programari-online` },
  { label: 'Noutati', href: `${SITE}/blog` },
  { label: 'Intreaba medicul', href: `${SITE}/intreaba-medicul` },
  { label: 'Autorecoltare', href: `${SITE}/autorecoltare` },
  { label: 'Acces Medici', href: 'https://rezultate.gralmedical.ro/Account/LogOn' },
  { label: 'Colaboratori', href: 'https://programari.gralmedical.ro/' },
  { label: 'Acces MM', href: 'https://medicinamuncii.gralmedical.ro/' },
  { label: 'Rezultate analize', href: `${SITE}/pacienti/istoric-pacienti` },
];

interface MainLink {
  label: string;
  href: string;
  items?: { label: string; href: string }[];
}

/** Main navigation row. Entries with `items` render a hover dropdown. */
const MAIN_LINKS: MainLink[] = [
  { label: 'Medici', href: `${SITE}/medici` },
  { label: 'Specialitati', href: `${SITE}/specialitati` },
  {
    label: 'Locatii',
    href: `${SITE}/locatii`,
    items: [
      { label: 'Laboratoare', href: `${SITE}/locatii#laborator` },
      { label: 'Clinici', href: `${SITE}/locatii#clinica` },
      { label: 'Centre de Recoltare', href: `${SITE}/locatii#crr` },
      { label: 'Spitale', href: `${SITE}/locatii#spital` },
      { label: 'Centrul de Chirurgie GRAL', href: `${SITE}/centrul-de-chirurgie` },
      { label: 'Patologia sânului', href: `${SITE}/centrul-de-excelenta-in-patologia-sanului` },
    ],
  },
  { label: 'Spitale', href: `${SITE}/spitale` },
  { label: 'Servicii', href: `${SITE}/servicii` },
  { label: 'Analize A-Z', href: `${SITE}/preturi#analize-laborator` },
  {
    label: 'Preturi',
    href: `${SITE}/preturi`,
    items: [
      { label: 'Analize Laborator', href: `${SITE}/preturi#analize-laborator` },
      { label: 'Imagistica', href: `${SITE}/preturi#imagistica` },
      { label: 'Consultatii si Investigatii', href: `${SITE}/preturi#consultatii-si-investigatii` },
    ],
  },
  { label: 'Oferte', href: `${SITE}/oferte` },
];

const ext = { target: '_blank', rel: 'noopener noreferrer' } as const;

/** gralmedical.ro "Contul meu" button — orange, white user icon + text. */
function AccountButton() {
  return (
    <NavLink
      to={routePaths.home}
      className="shine-image inline-flex shrink-0 items-center gap-1 rounded-full bg-accent px-4 py-1.5 text-xs font-normal text-white transition hover:brightness-95"
    >
      <img src="/brand/user-icon.svg" alt="" width={15} height={15} className="h-[14px] w-[14px]" />
      Contul meu
    </NavLink>
  );
}

/** Filled down-caret triangle — matches gralmedical.ro's Font Awesome `fa-caret-down`. */
function CaretDown() {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor" className="-mt-0.5 ml-0.5">
      <path d="M2 4h8L6 9z" />
    </svg>
  );
}

/**
 * gralmedical.ro-styled top header: utility links row + main bar (logo, call
 * center, marketing nav with dropdowns, search, and the account control). The
 * marketing nav is desktop-only (lg+); on smaller screens the portal sidebar
 * drawer handles navigation.
 */
interface SiteHeaderProps {
  /** Opens the portal (account) sidebar drawer on mobile. */
  onMenuClick?: () => void;
}

export function SiteHeader({ onMenuClick }: SiteHeaderProps) {
  return (
    <header className="sticky top-0 z-20 bg-surface/95 backdrop-blur">
      <div className="flex h-32 items-start gap-4 px-4 pt-4 sm:px-6">
        {/* Mobile sidebar toggle — only when a sidebar exists (logged-in shell) */}
        {onMenuClick && (
          <button
            type="button"
            onClick={onMenuClick}
            className="my-auto rounded-input p-2 text-ink-soft hover:bg-muted-100 lg:hidden"
            aria-label="Meniu"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            </svg>
          </button>
        )}

        {/* LEFT — logo (from the top) + call center */}
        <NavLink to={routePaths.home} className="shine-image shrink-0 self-start rounded-full pt-1" aria-label="GRAL Medical">
          <img src="/brand/gral-logo-mark.svg" alt="GRAL Medical" width={100} height={100} className="h-[100px] w-[100px]" />
        </NavLink>

        <a href={`tel:${CALL_CENTER.tel}`} className="hidden flex-col justify-center self-center leading-tight sm:flex">
          <span className="text-sm font-bold text-ink">Call Center</span>
          <span className="text-lg font-bold text-ink">{CALL_CENTER.label}</span>
        </a>

        {/* RIGHT — two stacked rows grouped together: utility links (top) +
            main nav (just below), top-aligned with the logo. */}
        <div className="ml-auto hidden flex-col gap-2.5 lg:flex">
          <div className="flex items-center justify-end gap-5 pt-2">
            {TOP_LINKS.map((l) => (
              <a key={l.label} href={l.href} {...ext} className="text-[11px] font-bold text-ink hover:text-accent">
                {l.label}
              </a>
            ))}
          </div>

          <nav className="flex items-center justify-end gap-[0.91vw] pt-3">
            {MAIN_LINKS.map((link) =>
              link.items ? (
                <div key={link.label} className="group relative">
                  <a href={link.href} {...ext} className="flex items-center gap-1 text-[16px] font-bold text-ink hover:text-accent group-hover:text-accent">
                    {link.label}
                    <CaretDown />
                  </a>
                  {/* gralmedical.ro effect (ported 1:1): each item animates its
                      height 0 → 44px on hover, clipped by the container's
                      overflow-hidden, so the list "unfolds". `pt-3` bridges the
                      hover gap; `invisible→visible` hides the box when collapsed. */}
                  <div className="invisible absolute left-0 top-full z-30 pt-3 group-hover:visible">
                    <div className="min-w-[260px] overflow-hidden rounded-card bg-surface shadow-[0px_14px_36px_-7px_#E2E4E8] pb-5">
                      {link.items.map((it) => (
                        <a
                          key={it.label}
                          href={it.href}
                          {...ext}
                          className="block h-0 overflow-hidden whitespace-nowrap px-5 text-[16px] font-bold leading-[28px] text-ink transition-[height] duration-1000 ease-in-out hover:text-accent group-hover:h-7"
                        >
                          {it.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <a key={link.label} href={link.href} {...ext} className="text-[16px] font-bold text-ink hover:text-accent">
                  {link.label}
                </a>
              ),
            )}

            <a href={`${SITE}/cautare-gral-medical`} {...ext} aria-label="Căutare" className="shrink-0">
              <img src="/brand/search-icon.svg" alt="" width={24} height={24} className="h-6 w-6" />
            </a>

            <AccountButton />
          </nav>
        </div>

        {/* Mobile account */}
        <div className="ml-auto self-center lg:hidden">
          <AccountButton />
        </div>
      </div>
    </header>
  );
}
